/**
 * Hosted-checkout bookkeeping (#150). The Buy page redirects the browser to the
 * gateway's payment page, so when the buyer lands back on /buy/complete we've
 * lost the in-memory request. We stash a small marker in `sessionStorage` before
 * leaving, then use it on return to find the matching minted buy in history.
 *
 * Pure + storage-injected so it unit-tests without a DOM (matches the app's
 * test style — logic in lib, no component render harness).
 */

/** What we remember across the redirect to recognize the buy on return. */
export interface CheckoutMarker {
  /** Sub-wallet that should be credited. */
  subWalletId: string;
  /** Wire-format smallest-unit amount, e.g. "2500". */
  amount: string;
  /** ISO timestamp the checkout was started — buys before this aren't ours. */
  startedAt: string;
}

/** sessionStorage key for the pending-checkout marker. */
export const CHECKOUT_MARKER_KEY = "ggcoin.checkout.pending";

/** Persist the marker before redirecting to the gateway. */
export function rememberCheckout(storage: Storage, marker: CheckoutMarker): void {
  storage.setItem(CHECKOUT_MARKER_KEY, JSON.stringify(marker));
}

/** Read + validate the marker, or `null` if absent/corrupt. */
export function readCheckout(storage: Storage): CheckoutMarker | null {
  const raw = storage.getItem(CHECKOUT_MARKER_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CheckoutMarker>;
    if (
      typeof parsed.subWalletId === "string" &&
      typeof parsed.amount === "string" &&
      typeof parsed.startedAt === "string"
    ) {
      return {
        subWalletId: parsed.subWalletId,
        amount: parsed.amount,
        startedAt: parsed.startedAt,
      };
    }
  } catch {
    // Corrupt marker — treat as none.
  }
  return null;
}

/** Drop the marker once the buy is resolved (or abandoned). */
export function clearCheckout(storage: Storage): void {
  storage.removeItem(CHECKOUT_MARKER_KEY);
}

/** The subset of a transaction-history item this module matches against. */
export interface BuyLike {
  kind: string;
  status: string;
  amount: string;
  toSubWalletId: string | null;
  createdAt: string;
}

/**
 * Clock-skew allowance for the checkout boundary. `createdAt` is server-stamped
 * and `startedAt` is client-stamped, so a few minutes of skew shouldn't make a
 * valid mint look stale. Generous on purpose — the sub-wallet + amount match
 * already scope the search to this purchase.
 */
const CHECKOUT_MATCH_CLOCK_SKEW_MS = 5 * 60 * 1000;

/** True if `createdAt` is at/after `startedAt`, allowing for clock skew. */
function isAtOrAfterCheckout(createdAt: string, startedAt: string): boolean {
  const created = Date.parse(createdAt);
  const started = Date.parse(startedAt);
  if (!Number.isFinite(created) || !Number.isFinite(started)) return false;
  return created >= started - CHECKOUT_MATCH_CLOCK_SKEW_MS;
}

/**
 * Find the buy this checkout produced: a `buy` into the marked sub-wallet, for
 * the marked amount, created at/after the checkout started (skew-tolerant). Newest
 * match wins — history is newest-first, so the first match is the freshest.
 * Returns `null` until the verified callback's mint shows up (the page keeps
 * polling). Timestamps are parsed, not string-compared, so differing ISO
 * formatting or minor client/server clock skew can't hide a valid mint.
 */
export function matchCheckoutBuy<T extends BuyLike>(
  transactions: readonly T[],
  marker: CheckoutMarker,
): T | null {
  return (
    transactions.find(
      (t) =>
        t.kind === "buy" &&
        t.toSubWalletId === marker.subWalletId &&
        t.amount === marker.amount &&
        isAtOrAfterCheckout(t.createdAt, marker.startedAt),
    ) ?? null
  );
}

/** Terminal/transient phases of the return page's wait for the mint. */
export type CheckoutPhase = "waiting" | "posted" | "failed" | "timeout";

/**
 * Decide the return page's phase from the matched buy (if any) and whether the
 * poll budget is exhausted. Pure so the route's outcome logic is unit-tested
 * without a DOM render harness:
 * - posted buy → `posted`; failed/reversed buy → `failed`
 * - no terminal buy yet → `waiting`, or `timeout` once polls run out
 */
export function resolveCheckoutPhase(buy: BuyLike | null, pollsExhausted: boolean): CheckoutPhase {
  if (buy) {
    if (buy.status === "posted") return "posted";
    if (buy.status === "failed" || buy.status === "reversed") return "failed";
  }
  return pollsExhausted ? "timeout" : "waiting";
}
